import React from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { S as TierS, A as TierA, B as TierB, C as TierC, D as TierD, E as TierE, F as TierF } from '../tiers';
import { Paper, Stack, Typography, Box } from '@mui/material';
import { storage } from '../firebase/config';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';

type Trap = {
  id: string;
  name: string;
  creators: string[];
  dateInvented: string;
  type: string,
  thumbnailUrl?: string;
  minigame: string,
  rating?: { average: number; count: number };
  tierlistRating?: { average: number; count: number };
  commentCount?: number;
  video?: { youtubeId?: string; embedUrl?: string; watchUrl?: string } | null;
};

const TrapRow: React.FC<{ trap: Trap }> = ({ trap }) => {
    const theme = useTheme();
    const surfaceBg = theme.palette.mode === 'light' ? '#ECECED' : '#262628';
    const surfaceBorder = alpha(theme.palette.light.main, 0.12);
    const hoverBg = theme.palette.mode === 'light' ? '#DEDEDE' : '#333335';
    const navigate = useNavigate();
  
    const avg = trap.tierlistRating?.average ?? trap.rating?.average ?? null;
    const cnt = trap.tierlistRating?.count ?? trap.rating?.count ?? 0;
    const rounded = Math.max(0, Math.min(6, Math.round(avg || 0)));
    const Tier = (
      rounded === 6 ? TierS :
      rounded === 5 ? TierA :
      rounded === 4 ? TierB :
      rounded === 3 ? TierC :
      rounded === 2 ? TierD :
      rounded === 1 ? TierE :
      TierF
    );
    const youTubeFallback = React.useMemo(() => {
      const id = trap.video?.youtubeId || (() => {
        const embed = trap.video?.embedUrl || '';
        const watch = trap.video?.watchUrl || '';
        const fromEmbed = /\/embed\/([^/?#]+)/.exec(embed)?.[1];
        const fromWatch = /[?&]v=([^&#]+)/.exec(watch)?.[1];
        return fromEmbed || fromWatch || '';
      })();
      return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : '';
    }, [trap.video]);
  
    const initialThumb = React.useMemo(() => {
      const raw = (trap.thumbnailUrl || '').trim();
      return /^https?:\/\//i.test(raw) ? raw : '';
    }, [trap.thumbnailUrl]);
    const [imgSrc, setImgSrc] = React.useState<string>(initialThumb || youTubeFallback);
    React.useEffect(() => {
      setImgSrc(initialThumb || youTubeFallback);
    }, [initialThumb, youTubeFallback]);

    React.useEffect(() => {
      let cancelled = false;
      const raw = (trap.thumbnailUrl || '').trim();
      if (!raw) return;

      const parseStoragePathFromUrl = (u: string): string | null => {
        if (!u) return null;
        if (u.startsWith('thumbnails/')) return u;
        if (u.startsWith('gs://')) {
          const m = /^gs:\/\/[^/]+\/(.+)$/.exec(u);
          return m ? m[1] : null;
        }
        const m = /^https?:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)(?:\?.*)?$/.exec(u);
        return m ? decodeURIComponent(m[1]) : null;
      };

      const needsSdkResolution = (u: string): boolean => {
        if (!u) return false;
        if (u.startsWith('thumbnails/') || u.startsWith('gs://')) return true;
        if (u.startsWith('https://firebasestorage.googleapis.com') && !u.includes('token=')) {
          return true;
        }
        return false;
      };

      (async () => {
        try {
          if (!needsSdkResolution(raw)) return;
          const path = parseStoragePathFromUrl(raw);
          if (!path) return;
          const url = await getDownloadURL(storageRef(storage, path));
          if (!cancelled) setImgSrc(url);
        } catch (_err) {
        }
      })();

      return () => { cancelled = true; };
    }, [trap.thumbnailUrl]);
    const invented = (() => {
      const d = new Date(trap.dateInvented);
      return isNaN(d.getTime()) ? trap.dateInvented : d.toLocaleDateString();
    })();
    const metaMinigame = trap.minigame;
    const metaType = trap.type;
  
    return (
      <Paper
        elevation={0}
        sx={{
          bgcolor: surfaceBg,
          border: `1px solid ${surfaceBorder}`,
          borderRadius: 2,
          p: { xs: 1, md: 1.25 },
          position: 'relative',
          transition: 'background-color 0ms linear, transform 150ms ease, border-color 120ms ease',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: hoverBg,
            borderColor: alpha(theme.palette.light.main, 0.18),
            transform: { xs: 'none', md: 'scale(1.02)' },
            zIndex: 1,
          },
        }}
        onClick={(e) => {
          e.preventDefault();
          navigate(`/trap/${trap.id}`);
        }}
      >
        <Typography
          variant="caption"
          sx={{ position: 'absolute', top: 8, right: 12, opacity: 0.7, fontWeight: 600, display: { xs: 'none', sm: 'block' } }}
        >
          {invented}
        </Typography>
        {(metaMinigame || metaType) && (
          <Stack
            spacing={0}
            sx={{ position: 'absolute', bottom: 8, right: 12, alignItems: 'flex-end' }}
          >
            {metaMinigame && (
              <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 600 }}>
                {metaMinigame}
              </Typography>
            )}
            {metaType && (
              <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 600 }}>
                {metaType}
              </Typography>
            )}
          </Stack>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.25, md: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Box
            component="img"
            src={imgSrc}
            alt={trap.name}
            sx={{
              width: { xs: '100%', sm: 220 },
              height: { xs: 'auto', sm: 120 },
              objectFit: { xs: 'contain', sm: 'cover' },
              borderRadius: 2,
              display: 'block',
            }}
            loading="lazy"
            onError={() => {
              if (youTubeFallback && imgSrc !== youTubeFallback) {
                setImgSrc(youTubeFallback);
              }
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1  }}>
            <Box sx={{ display: { xs: 'flex', sm: 'block' }, alignItems: { xs: 'baseline', sm: 'unset' }, justifyContent: { xs: 'space-between', sm: 'unset' }, gap: 1, minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: (theme) => (theme as any).typography.h6.fontSize, md: (theme) => (theme as any).typography.h5.fontSize }, minWidth: 0, flex: { xs: 1, sm: 'unset' } }} noWrap>
                {trap.name}
              </Typography>
              <Typography variant="caption" sx={{ display: { xs: 'inline', sm: 'none' }, opacity: 0.7, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {invented}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ display: 'block', mt: 0.25, opacity: 0.7, fontWeight: 500, fontSize: { xs: (theme) => (theme as any).typography.body2.fontSize, md: (theme) => (theme as any).typography.body1.fontSize } }} noWrap>
              {trap.creators.join(', ')}
            </Typography>
            <Box mt={0.9} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {cnt > 0 && <Tier size={28} />}
              <Typography variant="body2" sx={{ opacity: 0.5, fontWeight: 500, fontSize: { xs: (theme) => (theme as any).typography.caption.fontSize, md: (theme) => (theme as any).typography.body2.fontSize } }} noWrap>
                {cnt > 0 ? `${cnt} ratings` : 'No ratings'}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Paper>
    );
  };

  export default TrapRow;